import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid, Paper, Typography } from '@material-ui/core';
import { Charting, Loaders } from 'components';
import moment from 'moment';

import { StatsGrouping, useUserDailyStatsQuery } from 'api';
import { useCurrentUser, useNotify } from 'hooks';

import { LINE_CHART_HEIGHT } from '../../constants';
import LineChart from './LineChart';
import Value from './Value';
import LineChartEmptyState from './LineChartEmptyState';

const ExternalShares: FC = () => {
  const currentUser = useCurrentUser();
  const notify = useNotify();
  const { t } = useTranslation();

  const [variables, setVariables] = useState({
    grouping: StatsGrouping.Year,
    value: moment().year(),
  });

  const { data: statsData, isLoading } = useUserDailyStatsQuery(
    { user_id: currentUser.id, grouping: variables.grouping },
    {
      onError: notify.queryError,
    }
  );

  const { userDailyStats } = statsData || {};
  const { total_shares, total_clicks, data } = userDailyStats || {};

  function renderChart(dataKey: string, label: string, text: string) {
    if (isLoading) {
      return <Loaders.LineChart height={LINE_CHART_HEIGHT} />;
    }

    if (!isLoading && !data.length) {
      return <LineChartEmptyState text={text} />;
    }

    return <LineChart data={data} config={{ dataKey, label }} />;
  }

  return (
    <Paper>
      <Box paddingLeft={3} paddingTop={3} marginBottom={2}>
        <Typography variant="h6">{t('components:myShares')}</Typography>
      </Box>
      <Box paddingLeft={3} paddingRight={3} paddingBottom={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary">
              {t('common:share_plural')}
            </Typography>
            <Value isLoading={isLoading} value={total_shares} />
            <Box marginTop={2} position="relative">
              {renderChart(
                'shares',
                t('common:share_plural'),
                t('emptyStates:activityExternalSharesShares')
              )}
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="textSecondary">
              {t('common:click_plural')}
            </Typography>
            <Value isLoading={isLoading} value={total_clicks} />
            <Box marginTop={2} position="relative">
              {renderChart(
                'clicks',
                t('common:click_plural'),
                t('emptyStates:activityExternalSharesClicks')
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Divider />
      <Box paddingLeft={1}>
        <Charting.PeriodSelect variables={variables} onChange={changed => setVariables(changed)} />
      </Box>
    </Paper>
  );
};

export default ExternalShares;
